import React, { FC, memo } from 'react';
import MuiCircularProgress, { CircularProgressProps as MuiCircularProgressProps } from '@mui/material/CircularProgress';
import { BpDesignSystem } from '../../types';
import { styled } from '../../styles';
import { Box } from '../Box';

export type CircularProgressProps = BpDesignSystem.PropsWithChildren<MuiCircularProgressProps> & {
  shape?: 'bpe' | 'circle';
  size?: 'small' | 'medium' | 'large' | 'xLarge' | string | number;
  slotProps?: {
    wrapper: BpDesignSystem.Props;
  };
};

const DEFAULT_LARGE_SIZE = 40;

export const CircularProgress: FC<CircularProgressProps> = ({
  dataTest,
  shape = 'bpe',
  size = 'large',
  slotProps,
  ...rest
}) => {
  const targetSize = getSize(size);
  const wrapperProps = {
    'data-test': 'loading__component',
    ...slotProps?.wrapper,
    className: slotProps?.wrapper?.className
      ? `loading__component_wrapper ${slotProps?.wrapper.className}`
      : 'loading__component__wrapper'
  };
  if (!shape || shape === 'circle') {
    return (
      <StyledLoadingWrapper {...wrapperProps}>
        <MuiCircularProgress color="primary" data-test={dataTest} {...rest} size={targetSize} />
      </StyledLoadingWrapper>
    );
  }
  const targetThickness = (rest.thickness || 6) * 11;

  return (
    <StyledLoadingWrapper {...wrapperProps}>
      <StyledSvgContainer
        $size={targetSize}
        $thickness={targetThickness}
        sx={rest.sx}
        style={rest.style}
        dataTest={dataTest}
        className={rest.className}
      >
        <BpeShape />
      </StyledSvgContainer>
    </StyledLoadingWrapper>
  );
};

function getSize(size?: CircularProgressProps['size']) {
  if (!size) return DEFAULT_LARGE_SIZE;

  switch (size) {
    case 'small':
      return 18;
    case 'medium':
      return 28;
    case 'large':
      return DEFAULT_LARGE_SIZE;
    case 'xLarge':
      return 54;
  }
  return size;
}

const STROKE_WIDTH = 500;
const STROKE_GAP = 300;

const StyledLoadingWrapper = styled(Box)`
  color: ${(e) => e.theme.tokens.colorPrimary};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  position: relative;
`;

const StyledSvgContainer = styled(Box)<{ $size: number | string; $thickness: number }>`
    animation: rotate 4.4s linear infinite;
    height: ${({ $size }) => (typeof $size === 'string' ? $size : `${$size}px`)};
    width: ${({ $size }) => (typeof $size === 'string' ? $size : `${$size}px`)};

    polygon {
        animation: transform-shape 1.4s ease-in-out infinite;
        transform: scale(0.9) translate(30px, 30px);
        stroke-width: ${({ $thickness }) => $thickness}px;
        stroke-linejoin: round;
    }
    @keyframes transform-shape {
        0% {
            stroke-dasharray: ${STROKE_WIDTH}px, ${STROKE_GAP}px;
            stroke-dashoffset: 200px;
        }
        
        50% {
            stroke-dasharray: ${STROKE_WIDTH + 250}px, ${0}px;
            stroke-dashoffset: 100px;
        }
        
        100% {
            stroke-dasharray: ${STROKE_WIDTH}px, ${STROKE_GAP}px;
            stroke-dashoffset: -600px;
        }
    }
    @keyframes rotate {
        0% {
            transform-origin: 50% 50%;
        }
        
        100% {
            transform: rotate(360deg);
        }
    }
`;

// Import CircularProgress with more "user friendly" name
export const Loading = CircularProgress;

/** This shape is based on the BPE logo. It is used to make a circular progress indicator. */
const BpeShape = memo(() => (
  <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 512 512">
    <g>
      <polygon
        fill="transparent"
        points="25.045,128 256,0 486.955,128 486.955,384 256,512 25.045,384"
        stroke="url(#bpe-gradient)"
      />
    </g>
    <defs>
      <linearGradient
        id="bpe-gradient"
        x1="384.077"
        y1="348.34"
        x2="137.517"
        y2="170.07"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.01" stopColor="#004EFF" />
        <stop offset="0.16" stopColor="#0065FA" />
        <stop offset="0.38" stopColor="#0084F5" />
        <stop offset="0.6" stopColor="#009BF1" />
        <stop offset="0.81" stopColor="#00A8EE" />
        <stop offset="1" stopColor="#00ADEE" />
      </linearGradient>
    </defs>
  </svg>
));
